/**
 * Reincarcarea paginii cand tabul ruleaza cod mai vechi decat cel publicat.
 *
 * Doua situatii, aceeasi plasa contra buclei:
 *  - o bucata de cod ceruta de un tab vechi nu mai exista pe server (`vite:preloadError`);
 *  - `version.json` anunta alt build decat cel incarcat (`versiune-noua.ts`).
 *
 * 🔴 Fara plasa, un deploy rupt (sau un `version.json` ramas in urma pe server) ar reincarca
 * pagina la nesfarsit. De-aia fiecare motiv are voie la O reincarcare pe fereastra de timp;
 * a doua oara lasam eroarea sa se vada, ca pe orice alta.
 */

const CHEIE = 'ecotic:reincarcare';

/** Sub pragul asta, o a doua reincarcare pentru acelasi motiv e considerata bucla. */
export const FEREASTRA_REINCARCARE_MS = 60_000;

export interface UrmaReincarcare {
    motiv: string;
    la: number;
}

export function poateReincarca(
    motiv: string,
    acum: number,
    ultima: UrmaReincarcare | null,
    fereastraMs: number = FEREASTRA_REINCARCARE_MS
): boolean {
    if (!ultima) return true;
    if (ultima.motiv !== motiv) return true;
    return acum - ultima.la > fereastraMs;
}

function citesteUrma(): UrmaReincarcare | null {
    try {
        const brut = sessionStorage.getItem(CHEIE);
        if (!brut) return null;
        const urma = JSON.parse(brut);
        return typeof urma?.motiv === 'string' && typeof urma?.la === 'number' ? urma : null;
    } catch {
        return null;
    }
}

/**
 * Reincarca pagina, cel mult o data pe fereastra pentru acelasi motiv.
 * Intoarce `false` daca a refuzat (bucla) sau daca `sessionStorage` nu e disponibil — fara el
 * nu putem tine minte ca am reincarcat deja, deci nu riscam.
 */
export function reincarcaOData(motiv: string, fereastraMs: number = FEREASTRA_REINCARCARE_MS): boolean {
    const acum = Date.now();
    if (!poateReincarca(motiv, acum, citesteUrma(), fereastraMs)) return false;
    try {
        sessionStorage.setItem(CHEIE, JSON.stringify({ motiv, la: acum } satisfies UrmaReincarcare));
    } catch {
        return false;
    }
    window.location.reload();
    return true;
}

/**
 * Dupa un deploy, Azure SWA inlocuieste toate fisierele: un tab deschis dinainte cere, la primul
 * `import()` dinamic, o bucata care nu mai exista. Vite anunta asta prin `vite:preloadError`;
 * reincarcarea aduce `index.html` nou (servit cu revalidare), deci si bucatile noi.
 */
export function instaleazaReincarcareLaBucataLipsa(): void {
    window.addEventListener('vite:preloadError', (event) => {
        // Doar daca reincarcam inghitim eroarea; altfel o lasam sa ajunga la ErrorBoundary.
        if (reincarcaOData('bucata-lipsa')) event.preventDefault();
    });
}
